import { type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes } from "react";

export function Page({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="page">
      <div className="page-head">
        <h2>{title}</h2>
        <div className="page-actions">{actions}</div>
      </div>
      {children}
    </div>
  );
}

export function Card({ title, children, actions }: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="card">
      {(title || actions) && (
        <div className="card-head">
          {title && <h3 className="card-title">{title}</h3>}
          {actions && <div className="card-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small className="hint">{hint}</small>}
    </label>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} />;
}

export function Select({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  return <select {...props}>{children}</select>;
}

export function Button({ children, variant = "primary", ...props }: { children: ReactNode; variant?: "primary" | "ghost" | "danger" } & InputHTMLAttributes<HTMLButtonElement>) {
  return <button className={`btn b-${variant}`} {...(props as object)}>{children}</button>;
}

export function Money({ value, currency }: { value: string | number; currency?: string }) {
  const n = typeof value === "string" ? Number(value) : value;
  return <span className={n < 0 ? "money neg" : "money"}>{fmt(n, currency)}</span>;
}

function fmt(n: number, currency = "CAD"): string {
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(n);
}

export function Badge({ tone, children }: { tone: string; children: ReactNode }) {
  return <span className={`badge t-${tone || "none"}`}>{children}</span>;
}

export function Feedback({ ok, message }: { ok: boolean | null; message: string }) {
  if (ok === null || !message) return null;
  return <div className={ok ? "feedback ok" : "feedback bad"}>{message}</div>;
}

export function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <table className="data">
      <thead><tr>{head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
